import { container } from '@/app-modules/_ioc';
import { SERVICE_IDENTIFIERS } from '@/app-modules/_ioc/service-identifiers';
import { IPersistedStore } from '@/app-modules/store/persisted-store';
import { UserPreferences, UserWindowPreference, IWindowLaunchCondition, WindowType } from '@/app-modules/store/modules/user-preferences';
import { IModal } from '@/plugins/modal-manager/modal';

interface IModalLayoutStorageHandler {
  storeUserWindowPreferenceForModal(modal: IModal): void;
  disableAutoRestoreUserWindowPreferenceForModal(modal: IModal): void;
  disableAutoRestoreUserWindowPreferenceForAllModals(): void;
  getAllUserWindowPreferences(): UserWindowPreference[];
}

class NullModalLayoutStorageHandler implements IModalLayoutStorageHandler {
  public storeUserWindowPreferenceForModal(modal: IModal): void {
    // do nothing
  }

  public disableAutoRestoreUserWindowPreferenceForModal(modal: IModal): void {
    // do nothing
  }

  public disableAutoRestoreUserWindowPreferenceForAllModals(): void {
    // do nothing
  }

  public getAllUserWindowPreferences(): UserWindowPreference[] {
    return [];
  }
}

class ModalLayoutStorageHandler implements IModalLayoutStorageHandler {
  public storeUserWindowPreferenceForModal(modal: IModal): void {
    const userPreferences = this.getUserPreferences();
    let userWindowPreference = this.findUserWindowPreferenceForModal(modal, userPreferences);
    if (userWindowPreference) {
      userWindowPreference.location = modal.getLocationAndDimensionsInPixels();
      userWindowPreference.autoRestore = true;
    }
    else {
      userWindowPreference = this.getUserWindowPreferenceForModal(modal);
      userPreferences.windowPreferences.push(userWindowPreference);
    }
    this.saveUserPreferences(userPreferences);
  }

  public disableAutoRestoreUserWindowPreferenceForModal(modal: IModal): void {
    const userPreferences = this.getUserPreferences();
    const userWindowPreference = this.findUserWindowPreferenceForModal(modal, userPreferences);
    if (userWindowPreference) {
      userWindowPreference.autoRestore = false;
      this.saveUserPreferences(userPreferences);
    }
  }

  public disableAutoRestoreUserWindowPreferenceForAllModals(): void {
    const userPreferences = this.getUserPreferences();
    userPreferences.windowPreferences
                   .filter((userWindowPreference) => !this.isUserWindowPreferenceForCmChat(userWindowPreference))
                   .forEach((userWindowPreference) => userWindowPreference.autoRestore = false);
    this.saveUserPreferences(userPreferences);
  }

  private isUserWindowPreferenceForCmChat(userWindowPreference: UserWindowPreference): boolean {
    return userWindowPreference.type === WindowType.Chat && userWindowPreference.launchCondition.gameName === 'cmchat';
  }

  public getAllUserWindowPreferences(): UserWindowPreference[] {
    const userPreferences = this.getUserPreferences();
    return userPreferences.windowPreferences;
  }

  private getUserPreferences(): UserPreferences {
    const persistedStore = this.getPersistedStore();
    return persistedStore.Get(UserPreferences);
  }

  private saveUserPreferences(userPreferences: UserPreferences) {
    const persistedStore = this.getPersistedStore();
    persistedStore.Save(userPreferences);
  }

  private getPersistedStore(): IPersistedStore {
    return container.get(SERVICE_IDENTIFIERS.IPersistedStore) as IPersistedStore;
  }

  private getUserWindowPreferenceForModal(modal: IModal): UserWindowPreference {
    const userWindowPreference = new UserWindowPreference();
    const modalProps = modal.modalProps;
    userWindowPreference.type = modal.modalType;
    userWindowPreference.launchCondition = {
      gameName: modalProps.gameName,
      frontEndName: modalProps.frontEndName,
      clubName: modalProps.clubName,
      chatRoomName: modalProps.chatRoomName,
      id: modalProps.id
    };
    userWindowPreference.location = modal.getLocationAndDimensionsInPixels();
    userWindowPreference.autoRestore = true;
    return userWindowPreference;
  }

  private findUserWindowPreferenceForModal(modal: IModal, userPreferences: UserPreferences): UserWindowPreference {
    const index = this.findUserWindowPreferenceIndexForModal(modal, userPreferences);
    if (index >= 0) {
      return userPreferences.windowPreferences[index];
    }
  }

  private findUserWindowPreferenceIndexForModal(modal: IModal, userPreferences: UserPreferences): number {
    return userPreferences.windowPreferences.findIndex((userWindowPreference) => {
      if (userWindowPreference.type === modal.modalType) {
        if (this.areWindowLaunchConditionsEqual(userWindowPreference.launchCondition, modal.modalProps)) {
          return true;
        }
      }
      return false;
    });
  }

  private areWindowLaunchConditionsEqual(launchConditionA: IWindowLaunchCondition, launchConditionB: IWindowLaunchCondition): boolean {
    const stringsAreEqual = (stringA: string, stringB: string) => (stringA || '') === (stringB || '');

    if (!launchConditionA || !launchConditionB) {
      return false;
    }

    if (!stringsAreEqual(launchConditionA.gameName, launchConditionB.gameName)) {
      return false;
    }

    if (!stringsAreEqual(launchConditionA.clubName, launchConditionB.clubName)) {
      return false;
    }

    if (!stringsAreEqual(launchConditionA.chatRoomName, launchConditionB.chatRoomName)) {
      return false;
    }

    if (launchConditionA.id !== launchConditionB.id) {
      return false;
    }

    return true;
  }
}

export { IModalLayoutStorageHandler, ModalLayoutStorageHandler, NullModalLayoutStorageHandler };



// WEBPACK FOOTER //
// ./node_modules/tslint-loader??ref--0!./src/plugins/modal-manager/modal-layout-storage-handler.ts